import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { DataPersistence } from '@nrwl/angular';
import { grabIdFromParams } from 'src/shared/utilities/utility-functions';
import {
	map,
	withLatestFrom,
	mapTo,
	filter,
	concatMap,
	tap,
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { FieldTeamDetailComponent } from '../field-team-detail.component';
import { FieldTeamDetailPartialState } from './field-team-detail.reducer';
import { fieldTeamFormToFieldTeam } from '../field-team-detail.utility';
import * as FieldTeamDetailActions from './field-team-detail.actions';
import * as FieldTeamDetailSelectors from './field-team-detail.selectors';
import { PersonnelClient } from 'src/shared/api';
import { fieldTeamsFragments } from '../../field-teams.routes';
import { RailroadDetailPartialState } from 'src/admin-app/railroads/railroad-detail/+state/railroad-detail.reducer';
import { notifyAfterSaving } from 'src/shared/utilities/custom-rxjs-operators';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class FieldTeamDetailEffects {
	selectFieldTeam$ = createEffect(() =>
		this.dataPersistence.navigation(FieldTeamDetailComponent, {
			run: (
				r: ActivatedRouteSnapshot,
				_state: FieldTeamDetailPartialState
			) => {
				const fieldTeamId = grabIdFromParams(r.paramMap);

				return FieldTeamDetailActions.fieldTeamSelected({
					fieldTeamId,
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	loadFieldTeamFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FieldTeamDetailActions.fieldTeamSelected),
			map((action) => action.fieldTeamId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(FieldTeamDetailSelectors.getSelectedFieldTeam)
				),
				(_id, fieldTeam) => fieldTeam
			),
			filter(Boolean),
			map((fieldTeam) =>
				FieldTeamDetailActions.fieldTeamLoadedFromStore({ fieldTeam })
			)
		)
	);

	loadFieldTeamFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FieldTeamDetailActions.fieldTeamSelected),
			map((action) => action.fieldTeamId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(FieldTeamDetailSelectors.getSelectedFieldTeam)
				)
			),
			filter(([_id, fieldTeam]) => !fieldTeam),
			concatMap(([id, _fieldTeam]) =>
				this.personnelClient.getTeam(id).pipe(
					map((fieldTeam) =>
						FieldTeamDetailActions.fieldTeamLoadedFromApi({
							fieldTeam,
						})
					)
				)
			)
		)
	);

	loadSelectedRailroadFromApi$ = createEffect(() =>
		this.actions$.pipe(ofType(FieldTeamDetailActions.railroadSelected))
	);

	startNewFieldTeam$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FieldTeamDetailActions.fieldTeamSelected),
			map((action) => action.fieldTeamId),
			filter((id) => id === 0),
			mapTo(FieldTeamDetailActions.newFieldTeamStarted())
		)
	);

	saveFieldTeam$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			FieldTeamDetailActions.saveRequested,
			{
				run: (
					action: ReturnType<
						typeof FieldTeamDetailActions.saveRequested
					>,
					_state: FieldTeamDetailPartialState
				) => {
					const fieldTeam = fieldTeamFormToFieldTeam(
						action.formValue
					);

					return this.personnelClient.saveTeam(fieldTeam).pipe(
						map((id) =>
							fieldTeam.id === 0
								? FieldTeamDetailActions.newFieldTeamSaved({
										fieldTeam: { ...fieldTeam, id },
								  })
								: FieldTeamDetailActions.fieldTeamSaved({
										fieldTeam,
								  })
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof FieldTeamDetailActions.saveRequested
					>,
					error: any
				) => {
					return FieldTeamDetailActions.errorSavingFieldTeam({
						error,
					});
				},
			}
		)
	);

	throwIfError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					FieldTeamDetailActions.errorSavingFieldTeam,
					FieldTeamDetailActions.errorLoadingFieldTeam
				),
				map((action) => action.error),
				map((error) => {
					throw Error(error);
				})
			),
		{ dispatch: false }
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(FieldTeamDetailActions.fieldTeamSaved),
				withLatestFrom(
					this.store.pipe(
						select(FieldTeamDetailSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(fieldTeamsFragments.list))
			),
		{ dispatch: false }
	);

	navigateToCreatedFieldTeam$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(FieldTeamDetailActions.newFieldTeamSaved),
				map((action) => action.fieldTeam.id),
				tap((id) =>
					this.router.navigate(fieldTeamsFragments.detail(id))
				)
			),
		{ dispatch: false }
	);

	notifyAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(FieldTeamDetailActions.fieldTeamSaved),
				notifyAfterSaving(this.snackbar)
			),
		{ dispatch: false }
	);

	constructor(
		private readonly actions$: Actions,
		private readonly store: Store<
			FieldTeamDetailPartialState | RailroadDetailPartialState
		>,
		private readonly dataPersistence: DataPersistence<FieldTeamDetailPartialState>,
		private readonly personnelClient: PersonnelClient,
		private readonly router: Router,
		private readonly snackbar: MatSnackBar
	) {}
}
